/**
 * DetectedEntityTooltip Component
 * Shows accept/skip actions when clicking a detected entity in the document
 */

import { useEffect, useRef } from 'react'
import { Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DetectedEntity } from '@/types/document'
import { getEntityConfig } from '@/types/document'

interface DetectedEntityTooltipProps {
  entity: DetectedEntity | null
  position: { x: number; y: number } | null
  onAccept: (id: string) => void
  onReject: (id: string) => void
  onClose: () => void
}

export function DetectedEntityTooltip({
  entity,
  position,
  onAccept,
  onReject,
  onClose,
}: DetectedEntityTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null)

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!entity) return

    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    // Delay adding listener to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [entity, onClose])

  if (!entity || !position) return null

  const config = getEntityConfig(entity.suggestedOntologyType || entity.type)

  return (
    <div
      ref={tooltipRef}
      className={cn(
        'fixed z-50 bg-white rounded-lg shadow-lg border border-warm-200',
        'animate-in fade-in-0 zoom-in-95 duration-150'
      )}
      style={{
        left: position.x,
        top: position.y + 8, // 8px below the entity
      }}
    >
      <div className="p-3 min-w-[180px]">
        {/* Entity label */}
        <div className="font-medium text-sm text-text-primary mb-1 line-clamp-2">
          {entity.content}
        </div>

        {/* Type and confidence */}
        <div className="flex items-center gap-2 text-xs text-text-muted mb-3">
          <span style={{ color: config.color }}>{config.label}</span>
          <span>•</span>
          <span>{Math.round(entity.confidence * 100)}% confidence</span>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            className={cn(
              'flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium',
              'bg-business text-white hover:bg-business/90 transition-colors'
            )}
            onClick={() => {
              onAccept(entity.id)
              onClose()
            }}
          >
            <Check className="w-3 h-3" />
            Accept
          </button>
          <button
            className={cn(
              'flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium',
              'bg-warm-100 text-text-secondary hover:bg-warm-200 transition-colors'
            )}
            onClick={() => {
              onReject(entity.id)
              onClose()
            }}
          >
            <X className="w-3 h-3" />
            Skip
          </button>
        </div>
      </div>
    </div>
  )
}

export default DetectedEntityTooltip
